import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMobileOpen(false);
      }
    }
    if (mobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileOpen]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/90 backdrop-blur-md">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" onClick={() => setMobileOpen(false)}>
            <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 tracking-tight">
              GTBuy<span className="text-primary-500">.</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-5">
            <Link to="/" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer font-medium">
              Home
            </Link>

            {/* Shopping Guide Dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1 text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer font-medium">
                Shopping Guide
                <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-white rounded-lg shadow-lg border border-background-200/70 py-2 min-w-[200px]">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <Link to="/what-is-gtbuy-spreadsheet" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              GTBuy Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 text-background-50 dark:text-foreground-950 px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Visit Main Site
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <i className="ri-close-line w-6 h-6 flex items-center justify-center"></i>
            ) : (
              <i className="ri-menu-line w-6 h-6 flex items-center justify-center"></i>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-black/40 z-40" onClick={() => setMobileOpen(false)}></div>
      )}

      {/* Mobile Menu Panel */}
      <div
        ref={menuRef}
        className={`md:hidden fixed top-16 right-0 h-[calc(100vh-4rem)] w-72 bg-white z-50 transform transition-transform duration-300 shadow-xl ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          <div className="p-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
              Home
            </Link>

            <Link
              to="/what-is-gtbuy-spreadsheet"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-file-text-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
              GTBuy Spreadsheet Guide
            </Link>

            {/* Mobile Categories Accordion */}
            <div>
              <button
                onClick={() => setCatOpen(!catOpen)}
                className="flex items-center justify-between w-full px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-shopping-bag-3-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
                  Shopping Guide
                </span>
                {catOpen ? (
                  <i className="ri-arrow-up-s-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                ) : (
                  <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                )}
              </button>
              {catOpen && (
                <div className="ml-5 border-l-2 border-background-200/70 pl-3 space-y-0.5 mt-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-xs text-foreground-600 hover:bg-background-100 hover:text-primary-500 transition-colors cursor-pointer"
                    >
                      <span className="w-1 h-1 rounded-full bg-primary-400 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
              Visit Main Site
            </a>
          </div>

          <div className="mt-auto p-4 border-t border-background-200/70">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block w-full text-center bg-primary-500 text-background-50 dark:text-foreground-950 px-4 py-3 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now on Kaspreadsheet
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}